import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  Circle,
  ChevronUp,
  Sparkles,
  UserPlus,
  LayoutDashboard,
  Mail,
  Sliders,
  Share2,
  Rocket,
  X,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { getOnboardingState, dismissOnboarding, isOnboardingVisible } from '@/lib/onboarding';

const STEP_META: { id: number; title: string; icon: React.ElementType }[] = [
  { id: 1, title: 'Welcome', icon: Sparkles },
  { id: 2, title: 'Create a contact', icon: UserPlus },
  { id: 3, title: 'Explore Dashboard', icon: LayoutDashboard },
  { id: 4, title: 'Send a test email', icon: Mail },
  { id: 5, title: 'Review AI Settings', icon: Sliders },
  { id: 6, title: 'Explore Context Graph', icon: Share2 },
  { id: 7, title: "You're Ready!", icon: Rocket },
];

export function OnboardingChecklist() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [visible, setVisible] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  const refresh = useCallback(() => {
    const show = isOnboardingVisible();
    setVisible(show);
    if (show) {
      setCompletedSteps(getOnboardingState().completedSteps);
    }
  }, []);

  // Check visibility on mount + on focus (so completing steps on the onboarding page updates the widget)
  useEffect(() => {
    refresh();
    const handleFocus = () => refresh();
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'nexus:onboarding') refresh();
    };
    window.addEventListener('focus', handleFocus);
    window.addEventListener('storage', handleStorage);
    // Also poll periodically in case localStorage changed from the same tab
    const interval = setInterval(refresh, 2000);
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, [refresh]);

  if (!user || !visible) return null;

  const completedCount = completedSteps.length;
  const progressPct = (completedCount / 7) * 100;

  const handleDismiss = () => {
    dismissOnboarding();
    setVisible(false);
  };

  const handleStepClick = (stepId: number) => {
    navigate(`/getting-started?step=${stepId}`);
    setExpanded(false);
  };

  return (
    <div className="fixed bottom-4 left-4 z-50 sm:left-[calc(16rem+1rem)]">
      {/* Expanded panel */}
      {expanded && (
        <div className="mb-2 w-72 bg-surface-800 border border-surface-700 rounded-xl shadow-xl shadow-black/30 overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200">
          {/* Panel header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-surface-700">
            <span className="text-sm font-semibold text-surface-100">Getting Started</span>
            <button
              onClick={() => setExpanded(false)}
              className="text-surface-400 hover:text-surface-200 cursor-pointer"
              aria-label="Collapse"
            >
              <ChevronUp className="h-4 w-4" />
            </button>
          </div>

          {/* Steps list */}
          <div className="px-2 py-2 max-h-80 overflow-y-auto">
            {STEP_META.map((step) => {
              const done = completedSteps.includes(step.id);
              return (
                <button
                  key={step.id}
                  onClick={() => handleStepClick(step.id)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-surface-700/60 transition-colors cursor-pointer group"
                >
                  {done ? (
                    <CheckCircle2 className="h-4 w-4 text-nexus-400 flex-shrink-0" />
                  ) : (
                    <Circle className="h-4 w-4 text-surface-500 flex-shrink-0 group-hover:text-surface-400" />
                  )}
                  <span
                    className={`text-sm ${done ? 'text-surface-400 line-through' : 'text-surface-200'}`}
                  >
                    {step.title}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Dismiss */}
          <div className="px-4 py-3 border-t border-surface-700">
            <button
              onClick={handleDismiss}
              className="text-xs text-surface-500 hover:text-surface-300 transition-colors cursor-pointer"
            >
              I know what I'm doing
            </button>
          </div>
        </div>
      )}

      {/* Collapsed pill */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-3 bg-surface-800 border border-surface-700 rounded-full px-4 py-2.5 shadow-lg shadow-black/20 hover:border-surface-600 transition-all cursor-pointer group"
      >
        {/* Circular progress indicator */}
        <div className="relative w-7 h-7 flex-shrink-0">
          <svg className="w-7 h-7 -rotate-90" viewBox="0 0 28 28">
            <circle
              cx="14"
              cy="14"
              r="11"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              className="text-surface-700"
            />
            <circle
              cx="14"
              cy="14"
              r="11"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeDasharray={`${2 * Math.PI * 11}`}
              strokeDashoffset={`${2 * Math.PI * 11 * (1 - progressPct / 100)}`}
              strokeLinecap="round"
              className="text-nexus-400 transition-all duration-500"
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-surface-200">
            {completedCount}
          </span>
        </div>

        <span className="text-xs font-medium text-surface-300 group-hover:text-surface-100 transition-colors">
          {completedCount}/7 complete
        </span>

        <X
          className="h-3.5 w-3.5 text-surface-500 hover:text-surface-300 ml-1"
          onClick={(e) => {
            e.stopPropagation();
            handleDismiss();
          }}
        />
      </button>
    </div>
  );
}
